// This is the entrypoint to the portions of MC Student that are built
// using Webpack.


import react from 'react';
import { createRoot } from 'react-dom/client';
import underscore from 'underscore';
import { Alert } from '@instructure/ui-alerts';

import './lato/index.scss';
import './AutoloadErrorPane.scss';

import BubblesheetCoreSetup from 'BubblesheetCore/setup/main-setup.js';
import * as ValidationEntry from './ValidationEntry';
import * as LRNAssessWrapper from './LRNAssessWrapper';
import { StudentPreviewHeader } from './StudentPreviewHeader';
import { StudentPreviewFooter } from './StudentPreviewFooter';
import * as LegacyShims from './LegacyShims.js';
import { translationOverlayFromResponse } from './translations/translationOverlay';
import { localizeTestItems } from './translations/localizeTestItems';
import { takeStoredItemIndex } from './translations/languageSelection';
import { languageSelectorElement } from './translations/LanguageSelector';
import { switchLanguage } from './translations/languageSwitch';
import { ResultsDisplay } from './ResultsDisplay';
import { NextButton } from './NextButton';
import { DoneButton } from './DoneButton';
import sentrySetup from './setup/sentry';
import fullstorySetup from './setup/fullstory';
import analyticsSetup from './setup/analytics';
import i18nSetup from './setup/i18n.js';

i18nSetup();
sentrySetup();
fullstorySetup();
// We have to wait until we get analytics settings from Mothership via the check_student endpoint
// before actually initializing Heap, as it may be disabled for certain users due to privacy concerns.
window.mcAnalyticsSetup = analyticsSetup

// React 18 drops the stateless ReactDOM.render / unmountComponentAtNode API in
// favour of persistent roots created once per container. The Enyo kinds in this
// app call the old global API and re-render the same node repeatedly, so bridge
// that shape onto createRoot and cache one root per DOM node.
//
// Hazard: Enyo regenerates a control's innerHTML on re-render, detaching the DOM
// a cached root still believes it owns. Reusing that root reconciles against a
// dead fiber tree and commits nothing, leaving the panel blank with no error. So
// before reusing a root, confirm the container still holds rendered children; if
// Enyo emptied it, tear the stale root down and mount fresh against the live node.
const reactRoots = new WeakMap();
const ReactDOMCompat = {
  render(element, node, callback) {
    if (!node) return;
    let root = reactRoots.get(node);
    if (root && node.childNodes.length === 0) {
      root.unmount();
      reactRoots.delete(node);
      root = null;
    }
    if (!root) {
      root = createRoot(node);
      reactRoots.set(node, root);
    }
    root.render(element);
    if (typeof callback === 'function') {
      // Legacy ReactDOM.render ran the callback after commit; createRoot's
      // render is async, so defer to a microtask (queued after React's own
      // commit). App.js relies on this to advance the Enyo carousel
      // (appPanels.setIndex(4)) only once the assess panel's tree has mounted.
      queueMicrotask(callback);
    }
  },
  unmountComponentAtNode(node) {
    if (!node) return false;
    const root = reactRoots.get(node);
    if (!root) return false;
    root.unmount();
    reactRoots.delete(node);
    return true;
  },
};

window.React = react;
window.ReactDOM = ReactDOMCompat;
window._ = underscore;

window.InstUIBridge = {
  Alert,
};

BubblesheetCoreSetup();
window.ValidationEntry = ValidationEntry;
window.LRNAssessWrapper = LRNAssessWrapper;
window.ResultsDisplay = ResultsDisplay;
window.NextButton = NextButton;
window.DoneButton = DoneButton;
window.StudentPreviewHeader = StudentPreviewHeader;
window.StudentPreviewFooter = StudentPreviewFooter;

window.LegacyShims = LegacyShims;

// For the classic Enyo files (MCApi.js, App.js), which can't import ES modules.
window.Translations = {
  translationOverlayFromResponse,
  localizeTestItems,
  languageSelectorElement,
  switchLanguage,
  takeStoredItemIndex,
};

// We want to load tippy asynchronously because it's rather large, and
// is only used in infrequent situations
var tippyPromise = null;
window.__asyncRequestTippy = () => {
  if (!tippyPromise) {
    tippyPromise = import(/* webpackChunkName: "tippy" */ 'tippy.js');
  }
  return tippyPromise;
}
